import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import type { EmbedManageResponse } from '@wrapforce/shared';
import { QUOTE_SERVICES, type QuoteService } from '@wrapforce/shared/constants';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input, Label, FieldError } from '@/components/ui/input';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { useLocaleFormatters } from '@/lib/format';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { useMe } from '@/features/session/api';
import { apiErrorMessage } from '@/lib/api-errors';
import { useCreateEmbedWidget, useEmbedLogoUpload, useEmbedWidget, useRegenerateEmbedKey, useUpdateEmbedWidget } from './api';
import { invalidFieldsFromApiError } from './form-errors';

const STATUS_CLASS: Record<EmbedManageResponse['status'], string> = {
  active: 'bg-success/15 text-success',
  paused: 'bg-warning/15 text-warning',
  pending: 'bg-surface-2 text-muted',
  disabled: 'bg-danger/15 text-danger',
};

const ALLOWED_LOGO_TYPES = ['image/jpeg', 'image/png'];
const MAX_LOGO_BYTES = 1 * 1024 * 1024;
const PREVIEW_ORIGIN = (import.meta.env.VITE_MARKETING_URL as string | undefined) ?? 'https://wrapforce.io';

export function QuoteWidgetDetailPage() {
  const { t } = useTranslation('integrations');
  const { formatDateTime } = useLocaleFormatters();
  const { membership } = useActiveMembership();
  const { data: me } = useMe();
  const workspaceId = membership?.workspaceId ?? null;
  const { data, isLoading } = useEmbedWidget(workspaceId);
  const create = useCreateEmbedWidget(workspaceId);
  const update = useUpdateEmbedWidget(workspaceId);
  const regenerate = useRegenerateEmbedKey(workspaceId);
  const logoUpload = useEmbedLogoUpload(workspaceId);
  const { confirm, ConfirmDialog } = useConfirm();

  const [shopName, setShopName] = useState('');
  const [destinationEmail, setDestinationEmail] = useState('');
  const [services, setServices] = useState<QuoteService[]>(['wrap']);
  const [createErrors, setCreateErrors] = useState<{ shopName?: string; destinationEmail?: string; services?: string }>({});

  useEffect(() => {
    if (data?.widget === null) {
      setShopName(membership?.name ?? '');
      setDestinationEmail(me?.email ?? '');
    }
  }, [data, membership?.name, me?.email]);

  const [snippet, setSnippet] = useState<string | null>(null);
  const [editShopName, setEditShopName] = useState('');
  const [editAccent, setEditAccent] = useState('');
  const [editMode, setEditMode] = useState<'light' | 'dark' | 'auto'>('auto');
  const [editDomains, setEditDomains] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editServices, setEditServices] = useState<QuoteService[]>([]);
  const [editErrors, setEditErrors] = useState<{
    shopName?: string;
    destinationEmail?: string;
    accent?: string;
    allowedDomains?: string;
    services?: string;
  }>({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (data && data.widget !== null) {
      setSnippet(data.snippets.script);
      setEditShopName(data.widget.shopName);
      setEditAccent(data.widget.theme.accent);
      setEditMode(data.widget.theme.mode);
      setEditDomains((data.widget.allowedDomains ?? []).join('\n'));
      setEditEmail(data.widget.destinationEmail);
      setEditServices(data.widget.services);
    }
  }, [data]);

  function toggleService(service: QuoteService) {
    setServices((prev) => (prev.includes(service) ? prev.filter((s) => s !== service) : [...prev, service]));
    setCreateErrors((prev) => ({ ...prev, services: undefined }));
  }

  function toggleEditService(service: QuoteService) {
    setEditServices((prev) => (prev.includes(service) ? prev.filter((s) => s !== service) : [...prev, service]));
    setEditErrors((prev) => ({ ...prev, services: undefined }));
  }

  async function handleCreate() {
    const nextErrors: { shopName?: string; destinationEmail?: string; services?: string } = {};
    if (shopName.trim().length < 2) nextErrors.shopName = t('errors.shopNameMin');
    if (!/^\S+@\S+\.\S+$/.test(destinationEmail.trim())) nextErrors.destinationEmail = t('errors.emailInvalid');
    if (services.length === 0) nextErrors.services = t('errors.servicesMin');
    if (Object.keys(nextErrors).length > 0) {
      setCreateErrors(nextErrors);
      return;
    }
    try {
      await create.mutateAsync({ shopName: shopName.trim(), destinationEmail: destinationEmail.trim(), services });
      toast.success(t('quoteWidget.toast.created'));
    } catch (err) {
      const fields = invalidFieldsFromApiError(err);
      const mapped: { shopName?: string; destinationEmail?: string; services?: string } = {};
      if (fields.includes('shopName')) mapped.shopName = t('errors.shopNameMin');
      if (fields.includes('destinationEmail')) mapped.destinationEmail = t('errors.emailInvalid');
      if (fields.includes('services')) mapped.services = t('errors.servicesMin');
      if (Object.keys(mapped).length > 0) {
        setCreateErrors(mapped);
      } else {
        toast.error(t('quoteWidget.toast.createFailed'), { description: apiErrorMessage(err) });
      }
    }
  }

  async function handleSave() {
    const nextErrors: { shopName?: string; destinationEmail?: string; accent?: string; allowedDomains?: string; services?: string } = {};
    if (editShopName.trim().length < 2) nextErrors.shopName = t('errors.shopNameMin');
    if (!/^\S+@\S+\.\S+$/.test(editEmail.trim())) nextErrors.destinationEmail = t('errors.emailInvalid');
    if (!/^#[0-9a-fA-F]{6}$/.test(editAccent.trim())) nextErrors.accent = t('errors.accentInvalid');
    if (editServices.length === 0) nextErrors.services = t('errors.servicesMin');
    if (Object.keys(nextErrors).length > 0) {
      setEditErrors(nextErrors);
      return;
    }
    try {
      await update.mutateAsync({
        shopName: editShopName.trim(),
        theme: { accent: editAccent.trim(), mode: editMode },
        allowedDomains: editDomains
          .split('\n')
          .map((d) => d.trim())
          .filter(Boolean),
        destinationEmail: editEmail.trim(),
        services: editServices,
      });
      toast.success(t('quoteWidget.toast.saved'));
    } catch (err) {
      const fields = invalidFieldsFromApiError(err);
      const mapped: { shopName?: string; destinationEmail?: string; accent?: string; allowedDomains?: string; services?: string } = {};
      if (fields.includes('shopName')) mapped.shopName = t('errors.shopNameMin');
      if (fields.includes('destinationEmail')) mapped.destinationEmail = t('errors.emailInvalid');
      if (fields.includes('theme')) mapped.accent = t('errors.accentInvalid');
      if (fields.includes('allowedDomains')) mapped.allowedDomains = t('errors.checkField');
      if (fields.includes('services')) mapped.services = t('errors.servicesMin');
      if (Object.keys(mapped).length > 0) {
        setEditErrors(mapped);
      } else {
        toast.error(t('quoteWidget.toast.saveFailed'), { description: apiErrorMessage(err) });
      }
    }
  }

  async function handleToggleStatus(widget: EmbedManageResponse) {
    const nextStatus = widget.status === 'active' ? 'paused' : 'active';
    try {
      await update.mutateAsync({ status: nextStatus });
      toast.success(nextStatus === 'active' ? t('quoteWidget.toast.resumed') : t('quoteWidget.toast.paused'));
    } catch (err) {
      toast.error(t('quoteWidget.toast.saveFailed'), { description: apiErrorMessage(err) });
    }
  }

  async function handleRegenerate() {
    const confirmed = await confirm({
      title: t('quoteWidget.regenerateConfirmTitle'),
      description: t('quoteWidget.regenerateConfirmDescription'),
      confirmLabel: t('quoteWidget.regenerateKey'),
      cancelLabel: t('common:actions.cancel'),
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      const result = await regenerate.mutateAsync();
      setSnippet(result.snippets.script);
      toast.success(t('quoteWidget.toast.regenerated'));
    } catch (err) {
      toast.error(t('quoteWidget.toast.regenerateFailed'), { description: apiErrorMessage(err) });
    }
  }

  function handleCopy() {
    if (!snippet) return;
    navigator.clipboard.writeText(snippet);
    toast.success(t('quoteWidget.toast.snippetCopied'));
  }

  async function handleLogoFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ALLOWED_LOGO_TYPES.includes(file.type) || file.size > MAX_LOGO_BYTES) {
      toast.error(t('quoteWidget.toast.logoInvalid'));
      return;
    }
    setLogoUploading(true);
    try {
      const upload = await logoUpload.mutateAsync({ contentType: file.type as 'image/jpeg' | 'image/png', bytes: file.size });
      const putRes = await fetch(upload.uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      if (!putRes.ok) throw new Error(`upload failed (${putRes.status})`);
      await update.mutateAsync({ logoObjectKey: upload.objectKey });
      toast.success(t('quoteWidget.toast.logoUploaded'));
    } catch (err) {
      toast.error(t('quoteWidget.toast.logoUploadFailed'), { description: apiErrorMessage(err) });
    } finally {
      setLogoUploading(false);
    }
  }

  async function handleLogoRemove() {
    const confirmed = await confirm({
      title: t('quoteWidget.removeLogoConfirmTitle'),
      description: t('quoteWidget.removeLogoConfirmDescription'),
      confirmLabel: t('quoteWidget.removeLogo'),
      cancelLabel: t('common:actions.cancel'),
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      await update.mutateAsync({ logoObjectKey: null });
      toast.success(t('quoteWidget.toast.logoRemoved'));
    } catch (err) {
      toast.error(t('quoteWidget.toast.saveFailed'), { description: apiErrorMessage(err) });
    }
  }

  const widget = data?.widget ?? null;
  const previewUrl = widget ? `${PREVIEW_ORIGIN}/embed/quote/?key=${widget.embedKey}` : null;

  return (
    <div className="flex flex-col gap-6 p-6">
      <ConfirmDialog />
      <div>
        <Link to="/integrations" className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-ink">
          <ArrowLeft size={14} />
          {t('quoteWidget.back')}
        </Link>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <h1 className="font-display text-xl font-bold text-ink">{t('quoteWidget.title')}</h1>
          {widget ? (
            <span className={`px-2.5 py-1 text-xs font-bold uppercase ${STATUS_CLASS[widget.status]}`}>
              {t(`quoteWidget.status.${widget.status}`)}
            </span>
          ) : null}
          {widget ? (
            <div className="ml-auto flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPreviewOpen(true)}>
                {t('quoteWidget.preview')}
              </Button>
              <Button variant="outline" size="sm" loading={update.isPending} onClick={() => handleToggleStatus(widget)}>
                {widget.status === 'active' ? t('quoteWidget.pause') : t('quoteWidget.resume')}
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted">{t('common:actions.loading')}</p>
      ) : !widget ? (
        <Card className="max-w-xl">
          <CardHeader>
            <CardTitle>{t('quoteWidget.create')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <p className="text-sm text-muted">{t('quoteWidget.explainer')}</p>
            <div className="flex flex-col gap-2">
              <Label htmlFor="quote-widget-shop-name">{t('quoteWidget.shopName')}</Label>
              <Input
                id="quote-widget-shop-name"
                value={shopName}
                onChange={(e) => {
                  setShopName(e.target.value);
                  setCreateErrors((prev) => ({ ...prev, shopName: undefined }));
                }}
              />
              <FieldError>{createErrors.shopName}</FieldError>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="quote-widget-destination-email">{t('quoteWidget.destinationEmail')}</Label>
              <Input
                id="quote-widget-destination-email"
                type="email"
                value={destinationEmail}
                onChange={(e) => {
                  setDestinationEmail(e.target.value);
                  setCreateErrors((prev) => ({ ...prev, destinationEmail: undefined }));
                }}
              />
              <FieldError>{createErrors.destinationEmail}</FieldError>
            </div>
            <div className="flex flex-col gap-2">
              <Label>{t('quoteWidget.services')}</Label>
              <div className="flex flex-wrap gap-3">
                {QUOTE_SERVICES.map((service) => (
                  <label key={service} className="flex items-center gap-2 border border-border px-3 py-2 text-sm text-ink">
                    <input type="checkbox" checked={services.includes(service)} onChange={() => toggleService(service)} />
                    {t(`quoteWidget.serviceLabels.${service}`)}
                  </label>
                ))}
              </div>
              <FieldError>{createErrors.services}</FieldError>
              <p className="text-xs text-muted">{t('quoteWidget.servicesHint')}</p>
            </div>
            <Button className="w-fit" loading={create.isPending} onClick={handleCreate}>
              {t('quoteWidget.create')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="flex flex-col gap-4 lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>{t('quoteWidget.settings')}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="quote-widget-edit-shop-name">{t('quoteWidget.shopName')}</Label>
                    <Input
                      id="quote-widget-edit-shop-name"
                      value={editShopName}
                      onChange={(e) => {
                        setEditShopName(e.target.value);
                        setEditErrors((prev) => ({ ...prev, shopName: undefined }));
                      }}
                    />
                    <FieldError>{editErrors.shopName}</FieldError>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="quote-widget-edit-email">{t('quoteWidget.destinationEmail')}</Label>
                    <Input
                      id="quote-widget-edit-email"
                      type="email"
                      value={editEmail}
                      onChange={(e) => {
                        setEditEmail(e.target.value);
                        setEditErrors((prev) => ({ ...prev, destinationEmail: undefined }));
                      }}
                    />
                    <FieldError>{editErrors.destinationEmail}</FieldError>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="quote-widget-accent">{t('quoteWidget.accentColor')}</Label>
                    <div className="flex items-center gap-2">
                      <span className="h-10 w-10 shrink-0 border border-border" style={{ backgroundColor: editAccent }} />
                      <Input
                        id="quote-widget-accent"
                        value={editAccent}
                        onChange={(e) => {
                          setEditAccent(e.target.value);
                          setEditErrors((prev) => ({ ...prev, accent: undefined }));
                        }}
                      />
                    </div>
                    <FieldError>{editErrors.accent}</FieldError>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="quote-widget-mode">{t('quoteWidget.mode')}</Label>
                    <select
                      id="quote-widget-mode"
                      value={editMode}
                      onChange={(e) => setEditMode(e.target.value as 'light' | 'dark' | 'auto')}
                      className="h-10 w-full border border-border bg-surface px-3.5 text-sm text-ink outline-none focus-visible:border-accent"
                    >
                      <option value="auto">{t('quoteWidget.modeAuto')}</option>
                      <option value="light">{t('quoteWidget.modeLight')}</option>
                      <option value="dark">{t('quoteWidget.modeDark')}</option>
                    </select>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Label>{t('quoteWidget.services')}</Label>
                  <div className="flex flex-wrap gap-3">
                    {QUOTE_SERVICES.map((service) => (
                      <label key={service} className="flex items-center gap-2 border border-border px-3 py-2 text-sm text-ink">
                        <input type="checkbox" checked={editServices.includes(service)} onChange={() => toggleEditService(service)} />
                        {t(`quoteWidget.serviceLabels.${service}`)}
                      </label>
                    ))}
                  </div>
                  <FieldError>{editErrors.services}</FieldError>
                  <p className="text-xs text-muted">{t('quoteWidget.servicesHint')}</p>
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="quote-widget-domains">{t('quoteWidget.allowedDomains')}</Label>
                  <textarea
                    id="quote-widget-domains"
                    value={editDomains}
                    onChange={(e) => {
                      setEditDomains(e.target.value);
                      setEditErrors((prev) => ({ ...prev, allowedDomains: undefined }));
                    }}
                    placeholder={t('quoteWidget.allowedDomainsPlaceholder')}
                    rows={3}
                    className="w-full border border-border bg-surface px-3.5 py-2 text-sm text-ink outline-none placeholder:text-muted focus-visible:border-accent"
                  />
                  <FieldError>{editErrors.allowedDomains}</FieldError>
                  <p className="text-xs text-muted">{t('quoteWidget.allowedDomainsHint')}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" loading={update.isPending} onClick={handleSave}>
                    {t('common:actions.save')}
                  </Button>
                  <Button size="sm" variant="outline" loading={regenerate.isPending} onClick={handleRegenerate}>
                    {t('quoteWidget.regenerateKey')}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('quoteWidget.embedSnippet')}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-2">
                <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-all bg-surface-2 p-3 font-mono text-xs">{snippet}</pre>
                <Button variant="outline" size="sm" className="w-fit" onClick={handleCopy}>
                  {t('quoteWidget.copy')}
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="flex flex-col gap-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('quoteWidget.stats')}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <div className="grid grid-cols-3 gap-3 text-center text-sm">
                  <div>
                    <div className="text-lg font-bold text-ink">{widget.viewsCount}</div>
                    <div className="text-xs text-muted">{t('quoteWidget.views')}</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-ink">{widget.startsCount}</div>
                    <div className="text-xs text-muted">{t('quoteWidget.starts')}</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-ink">{widget.submissionsCount}</div>
                    <div className="text-xs text-muted">{t('quoteWidget.submissions')}</div>
                  </div>
                </div>
                <p className="text-xs text-muted">
                  {t('quoteWidget.lastSubmission')}:{' '}
                  {widget.lastSubmissionAt ? formatDateTime(new Date(widget.lastSubmissionAt)) : t('quoteWidget.noSubmissionsYet')}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('quoteWidget.logo')}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                <div className="flex items-center gap-4">
                  {widget.logoUrl ? (
                    <img src={widget.logoUrl} alt={t('quoteWidget.logo')} className="h-16 w-16 border border-border bg-surface object-contain" />
                  ) : (
                    <div className="grid h-16 w-16 place-items-center border border-dashed border-border bg-surface-2 text-center text-xs text-muted">
                      {t('quoteWidget.noLogo')}
                    </div>
                  )}
                  <div className="flex flex-col items-start gap-2">
                    <Button variant="outline" size="sm" loading={logoUploading} onClick={() => logoInputRef.current?.click()}>
                      {widget.logoUrl ? t('quoteWidget.replaceLogo') : t('quoteWidget.uploadLogo')}
                    </Button>
                    {widget.logoUrl ? (
                      <button type="button" className="text-xs text-danger hover:underline" onClick={handleLogoRemove}>
                        {t('quoteWidget.removeLogo')}
                      </button>
                    ) : null}
                  </div>
                </div>
                <p className="text-xs text-muted">{t('quoteWidget.logoHint')}</p>
                <input ref={logoInputRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={handleLogoFile} />
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {previewUrl ? (
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen} title={t('quoteWidget.preview')} description={t('quoteWidget.previewDescription')}>
          <div className="flex flex-col gap-3">
            <iframe src={previewOpen ? previewUrl : undefined} title={t('quoteWidget.preview')} className="h-[520px] w-full border border-border bg-surface" />
            <a
              href={previewUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex w-fit items-center gap-1.5 text-sm text-accent hover:underline"
            >
              <ExternalLink size={14} />
              {t('quoteWidget.openInNewTab')}
            </a>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
